import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { auth } from '../firebase';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import {
  FaArrowLeft, FaChartLine, FaDollarSign, FaBuilding,
  FaWrench, FaBolt, FaShieldAlt, FaFileInvoiceDollar, FaEllipsisH,
  FaArrowUp, FaArrowDown, FaHome, FaMoneyBillWave, FaPercentage, FaTimes,
  FaFilter, FaCalendarAlt
} from 'react-icons/fa';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import './Sidebar.css';
import './Navbar.css';
import './ReportDetails.css';
import LoadingScreen from './LoadingScreen';

const BASE_URL = 'https://api.rside.app';
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function getTimeParams(filterMode, customMonths, customYears) {
  const now = new Date();
  const em = now.getMonth() + 1;
  const ey = now.getFullYear();
  if (filterMode === 'current_month') {
    return { start_month: em, end_month: em, start_year: ey, end_year: ey };
  }
  if (filterMode === 'trailing_12') {
    const d = new Date(now); d.setMonth(d.getMonth() - 11);
    return { start_month: d.getMonth() + 1, end_month: em, start_year: d.getFullYear(), end_year: ey };
  }
  if (filterMode === 'custom' && customMonths && customYears) {
    return { start_month: customMonths[0] + 1, end_month: customMonths[1] + 1, start_year: customYears[0], end_year: customYears[1] };
  }
  return { start_month: 1, end_month: em, start_year: ey, end_year: ey }; // YTD default
}

function formatMonth(m) {
  if (!m) return '';
  if (typeof m === 'number') return MONTHS[m - 1] || String(m);
  const s = String(m);
  // Exact "YYYY-MM" format (e.g. "2026-06") OR format strings like "%Y-%m"
  const exact = s.match(/^(\d{4})-(\d{1,2})$/);
  if (exact) return MONTHS[parseInt(exact[2], 10) - 1] || s;
  // Any dash-delimited — take last numeric segment
  if (s.includes('-')) {
    const parts = s.split('-');
    for (let i = parts.length - 1; i >= 0; i--) {
      const n = parseInt(parts[i], 10);
      if (!isNaN(n) && n >= 1 && n <= 12) return MONTHS[n - 1];
    }
  }
  const p = new Date(s);
  return isNaN(p) ? s : MONTHS[p.getMonth()];
}

function safeJson(res) {
  return res.status === 'fulfilled' && res.value.ok ? res.value.json().catch(() => null) : Promise.resolve(null);
}

function fmt(v) {
  return Math.abs(Number(v) || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function fmtFull(v) {
  return Math.abs(Number(v) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtPct(v) {
  const n = Number(v);
  return isNaN(n) ? '—' : `${n.toFixed(2)}%`;
}

function getCategoryIcon(category) {
  const c = (category || '').toLowerCase();
  if (c.includes('maintenance') || c.includes('repair')) return <FaWrench />;
  if (c.includes('utilities') || c.includes('electric')) return <FaBolt />;
  if (c.includes('insurance')) return <FaShieldAlt />;
  if (c.includes('tax')) return <FaFileInvoiceDollar />;
  return <FaEllipsisH />;
}

function getBadge(type, value) {
  const n = Number(value) || 0;
  if (type === 'net_income') return n >= 0 ? { label: 'Profitable', cls: 'rd-mbadge-green' } : { label: 'Loss', cls: 'rd-mbadge-red' };
  if (type === 'noi')        return n >= 0 ? { label: 'Positive',   cls: 'rd-mbadge-green' } : { label: 'Negative', cls: 'rd-mbadge-red' };
  if (type === 'coc') {
    if (n > 12) return { label: 'Excellent', cls: 'rd-mbadge-green' };
    if (n > 8)  return { label: 'Good',      cls: 'rd-mbadge-green' };
    if (n > 0)  return { label: 'Fair',      cls: 'rd-mbadge-orange' };
    return { label: 'Poor', cls: 'rd-mbadge-red' };
  }
  if (type === 'cap_rate') {
    if (n > 10) return { label: 'Strong', cls: 'rd-mbadge-green' };
    if (n > 6)  return { label: 'Good',   cls: 'rd-mbadge-green' };
    if (n > 3)  return { label: 'Fair',   cls: 'rd-mbadge-orange' };
    return { label: 'Weak', cls: 'rd-mbadge-red' };
  }
  if (type === 'occupancy') {
    if (n > 90) return { label: 'Excellent', cls: 'rd-mbadge-green' };
    if (n > 75) return { label: 'Good',      cls: 'rd-mbadge-green' };
    if (n > 50) return { label: 'Fair',      cls: 'rd-mbadge-orange' };
    return { label: 'Low', cls: 'rd-mbadge-red' };
  }
  if (type === 'expense_ratio') {
    if (n < 35) return { label: 'Excellent', cls: 'rd-mbadge-green' };
    if (n < 60) return { label: 'Good',      cls: 'rd-mbadge-green' };
    return { label: 'High', cls: 'rd-mbadge-red' };
  }
  return { label: '—', cls: '' };
}

function ReportDetails() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [initials, setInitials]   = useState('');
  const [pmEmail, setPmEmail]     = useState('');
  const [property, setProperty]   = useState(null);
  const [loading, setLoading]         = useState(true);
  const [activeModal, setActiveModal] = useState(null);

  // Filter state
  const cy = new Date().getFullYear();
  const [filterMode, setFilterMode]       = useState('ytd');
  const [showFilters, setShowFilters]     = useState(false);
  const [pendingMonths, setPendingMonths] = useState([0, 11]);
  const [pendingYears, setPendingYears]   = useState([cy, cy]);
  const [customMonths, setCustomMonths]   = useState([0, 11]);
  const [customYears, setCustomYears]     = useState([cy, cy]);
  const [filterTrigger, setFilterTrigger] = useState(0);
  // Ref so the filterTrigger effect always sees the latest filter values (avoids stale closure)
  const filterStateRef = useRef({ mode: 'ytd', cMonths: [0, 11], cYears: [cy, cy] });

  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const toggleCategory = (cat) => setExpandedCategories(prev => { const n = new Set(prev); n.has(cat) ? n.delete(cat) : n.add(cat); return n; });

  const [cashFlowSummary, setCashFlowSummary] = useState({ income: 0, expenses: 0, netCashFlow: 0 });
  const [monthlyCashFlowData, setMonthlyCashFlowData] = useState([]);
  const [monthlyIncomeData, setMonthlyIncomeData]     = useState([]);
  const [monthlyExpensesData, setMonthlyExpensesData] = useState([]);
  const [expensesByCategory, setExpensesByCategory]   = useState([]);

  const [noiData, setNoiData]               = useState(null);
  const [capRateData, setCapRateData]       = useState(null);
  const [cashOnCash, setCashOnCash]         = useState(null);
  const [netIncomeData, setNetIncomeData]   = useState(null);
  const [occupancyData, setOccupancyData]   = useState(null);
  const [propertyFinancials, setPropertyFinancials] = useState(null);
  const [rentPayments, setRentPayments]     = useState([]);
  const [forgivenessItems, setForgivenessItems] = useState([]);
  const [expensesList, setExpensesList]     = useState([]);
  const [incomeByCat, setIncomeByCat]       = useState([]);

  const closeModal = () => setActiveModal(null);

  const fetchReportData = useCallback(async (propertyId, email, propName, mode, cMonths, cYears) => {
    const tp  = getTimeParams(mode, cMonths, cYears);
    const tqs = `property_id=${propertyId}&start_month=${tp.start_month}&end_month=${tp.end_month}&start_year=${tp.start_year}&end_year=${tp.end_year}`;
    const dqs = `start_month=${tp.start_month}&end_month=${tp.end_month}&start_year=${tp.start_year}&end_year=${tp.end_year}`;

    // Clear stale data so old filter values never bleed into new period display
    setCashFlowSummary({ income: 0, expenses: 0, netCashFlow: 0 });
    setMonthlyCashFlowData([]); setMonthlyIncomeData([]); setMonthlyExpensesData([]);
    setExpensesByCategory([]); setNoiData(null); setCashOnCash(null);
    setNetIncomeData(null); setOccupancyData(null);
    setRentPayments([]); setForgivenessItems([]); setExpensesList([]); setIncomeByCat([]);

    const results = await Promise.allSettled([
      fetch(`${BASE_URL}/PropertyCashFlowYTD?${tqs}`),                                                           // 0
      fetch(`${BASE_URL}/PropertyMonthlyCashFlowYTD?${tqs}`),                                                    // 1
      fetch(`${BASE_URL}/PropertyMonthlyIncomeYTD?${tqs}`),                                                      // 2
      fetch(`${BASE_URL}/PropertyMonthlyExpensesYTD?${tqs}`),                                                    // 3
      fetch(`${BASE_URL}/ExpensesByCategoryYTD?${tqs}`),                                                         // 4
      fetch(`${BASE_URL}/NOIByProperty?${tqs}`),                                                                  // 5
      fetch(`${BASE_URL}/CapRateByProperty?property_id=${propertyId}&pm_email=${encodeURIComponent(email)}&mode=YTD&start_month=${tp.start_month}&end_month=${tp.end_month}&start_year=${tp.start_year}&end_year=${tp.end_year}`), // 6
      fetch(`${BASE_URL}/CashOnCashByProperty?${tqs}`),                                                           // 7
      fetch(`${BASE_URL}/PropertyFinancials?property_id=${propertyId}`),                                         // 8
      fetch(`${BASE_URL}/NetIncomeByProperty?${tqs}`),                                                           // 9
      fetch(`${BASE_URL}/PropertyOccupancyLast12Months?${tqs}`),                                                 // 10
      fetch(`${BASE_URL}/ListRentPayments?pm_email=${encodeURIComponent(email)}&${dqs}`),                        // 11
      fetch(`${BASE_URL}/ListExpense?pm_email=${encodeURIComponent(email)}&${dqs}`),                             // 12
      fetch(`${BASE_URL}/IncomeBreakdownByCategory?${tqs}`),                                                     // 13
    ]);

    const jsons = await Promise.all(results.map(r => safeJson(r)));
    const [
      cashFlow, monthlyFlow, monthlyInc, monthlyExp, expCat,
      noi, capRate, coc, fin, netInc, occ, rentPay, expList, incBreak
    ] = jsons;

    if (cashFlow) {
      setCashFlowSummary({
        income:      Number(cashFlow.total_rent_ytd)     || 0,
        expenses:    Number(cashFlow.total_expenses_ytd) || 0,
        netCashFlow: Number(cashFlow.cash_flow_ytd)      || 0,
      });
    }

    if (monthlyFlow?.monthly_cash_flow_ytd) {
      setMonthlyCashFlowData(monthlyFlow.monthly_cash_flow_ytd.map(i => ({
        rawMonth: String(i.month), month: formatMonth(i.month),
        value: Number(i.cash_flow) || 0,
      })).sort((a,b) => a.rawMonth.localeCompare(b.rawMonth)));
    }

    if (monthlyInc?.monthly_rent_ytd) {
      setMonthlyIncomeData(monthlyInc.monthly_rent_ytd.map(i => ({
        rawMonth: String(i.month), month: formatMonth(i.month),
        value: Number(i.total_rent_collected || i.rent || 0),
      })).sort((a,b) => a.rawMonth.localeCompare(b.rawMonth)));
    }

    if (monthlyExp?.monthly_expenses_ytd) {
      setMonthlyExpensesData(monthlyExp.monthly_expenses_ytd.map(i => ({
        rawMonth: String(i.month), month: formatMonth(i.month),
        value: Number(i.total_expenses || i.expenses || 0),
      })).sort((a,b) => a.rawMonth.localeCompare(b.rawMonth)));
    }

    if (Array.isArray(expCat?.expenses_by_category_ytd)) {
      setExpensesByCategory(
        expCat.expenses_by_category_ytd
          .map(i => ({ category: i.Category || i.category || 'Other', amount: Number(i.total_expenses_ytd || i.amount || 0) }))
          .filter(i => i.amount > 0)
      );
    }

    if (noi)    setNoiData(noi);
    if (capRate) setCapRateData(capRate);
    if (coc)    setCashOnCash(coc);
    if (fin)    setPropertyFinancials(fin.financials?.[0] || fin);
    if (netInc) setNetIncomeData(netInc);
    if (occ)    setOccupancyData(occ);
    if (incBreak?.income_by_category) {
      setIncomeByCat(incBreak.income_by_category.map(c => ({ name: c.category, value: Number(c.amount) })));
    }

    if (rentPay) {
      const list = Array.isArray(rentPay.items) ? rentPay.items
        : Array.isArray(rentPay) ? rentPay
        : Array.isArray(rentPay.payments) ? rentPay.payments
        : Array.isArray(rentPay.rent_payments) ? rentPay.rent_payments
        : Object.values(rentPay).filter(v => Array.isArray(v)).flat();
      const filtered = propName
        ? list.filter(r => (r.Property_Name || r.property_name || '').toLowerCase() === propName.toLowerCase())
        : list;
      const forgiven = filtered.filter(r => r.item_type === 'rent_forgiveness' || r.Category === 'RentForgiveness');
      setForgivenessItems(forgiven);
      setRentPayments(filtered.filter(r => r.item_type !== 'rent_forgiveness' && r.Category !== 'RentForgiveness').sort((a, b) => new Date(b.Paydate || b.timestamp || b.paydate || b.Date || 0) - new Date(a.Paydate || a.timestamp || a.paydate || a.Date || 0)));
    }

    if (expList) {
      const list = Array.isArray(expList) ? expList
        : Array.isArray(expList.expenses) ? expList.expenses
        : Array.isArray(expList.Expenses) ? expList.Expenses
        : Object.values(expList).filter(v => Array.isArray(v)).flat();
      const filtered = propName
        ? list.filter(e => (e.Property_Name || e.property_name || '').toLowerCase() === propName.toLowerCase())
        : list;
      setExpensesList(filtered.sort((a, b) => new Date(b.Paydate || b.paydate || b.Date || 0) - new Date(a.Paydate || a.paydate || a.Date || 0)));
    }
  }, []);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) return navigate('/login');
      try {
        const res  = await fetch(`${BASE_URL}/PropertyManagerName?email=${user.email}`);
        const data = await res.json();
        const parts = data.Full_Name?.split(' ') || [];
        setInitials((parts[0]?.[0] || '') + (parts.at(-1)?.[0] || ''));
        setPmEmail(user.email);

        const propRes  = await fetch(`${BASE_URL}/GetAllProperties?full_name=${encodeURIComponent(data.Full_Name)}&Email=${encodeURIComponent(user.email)}`);
        if (!propRes.ok) return;
        const propData = await propRes.json();
        const found    = (propData.Properties || []).find(p => String(p.Property_ID) === String(id));
        if (!found) return;

        const prop = {
          id:            found.Property_ID,
          name:          found.Property_Name,
          propertyType:  parseInt(found.Num_Of_Units) > 1 ? 'Multi Family Home' : 'Single Family Home',
          numberOfUnits: found.Num_Of_Units,
          status:        found.Status || 'Vacant',
          address:       found.Street_Address,
        };
        setProperty(prop);
        await fetchReportData(found.Property_ID, user.email, found.Property_Name, 'ytd', [0, 11], [new Date().getFullYear(), new Date().getFullYear()]);
      } catch (err) {
        console.error('Error loading report:', err);
      } finally {
        setLoading(false);
      }
    });
    return unsub;
  }, [navigate, id, fetchReportData]);

  useEffect(() => {
    if (!property || !pmEmail || filterTrigger === 0) return;
    const { mode, cMonths, cYears } = filterStateRef.current;
    fetchReportData(property.id, pmEmail, property.name, mode, cMonths, cYears);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterTrigger]);

  const applyFilter = (mode, cMonths, cYears) => {
    const resolvedMonths = cMonths || customMonths;
    const resolvedYears  = cYears  || customYears;
    filterStateRef.current = { mode, cMonths: resolvedMonths, cYears: resolvedYears };
    setFilterMode(mode);
    if (mode === 'custom') { setCustomMonths(resolvedMonths); setCustomYears(resolvedYears); }
    setFilterTrigger(t => t + 1);
    setShowFilters(false);
  };

  // ── Expense grouping ─────────────────────────────
  const OP_COST_SUBS = new Set(['HOA', 'Tax', 'Insurance', 'Mortgage']);
  const UTILITY_SUBS = new Set(['Water', 'Gas', 'Electricity', 'Trash', 'Sewer']);
  const getParentCat = (cat) => {
    if (OP_COST_SUBS.has(cat) || cat === 'Operation Cost') return 'Operation Cost';
    if (UTILITY_SUBS.has(cat) || cat === 'Utility') return 'Utility';
    return cat;
  };
  const groupedExpenses = expensesList.reduce((acc, e) => {
    const cat    = e.Category || e.category || 'Other';
    const parent = getParentCat(cat);
    if (!acc[parent]) acc[parent] = { total: 0, subs: {} };
    acc[parent].total += Number(e.Amount || 0);
    if (cat !== parent) {
      // Named sub-type (HOA, Tax, Water, Gas, etc.)
      acc[parent].subs[cat] = (acc[parent].subs[cat] || 0) + Number(e.Amount || 0);
    } else if (cat === 'Operation Cost' || cat === 'Utility') {
      // General sub-type stored as parent category name
      acc[parent].subs['General'] = (acc[parent].subs['General'] || 0) + Number(e.Amount || 0);
    }
    return acc;
  }, {});
  const CAT_ORDER  = ['Repair', 'Operation Cost', 'Utility', 'Maintenance', 'Renovation', 'Other'];
  const sortedCats = [...CAT_ORDER.filter(c => groupedExpenses[c]),
                      ...Object.keys(groupedExpenses).filter(c => !CAT_ORDER.includes(c))];
  const expBreakdownTotal = Object.values(groupedExpenses).reduce((s, g) => s + g.total, 0) || cashFlowSummary.expenses;
  const CAT_CONFIG = {
    'Repair':         { icon: <FaWrench />,          color: '#2563eb' },
    'Operation Cost': { icon: <FaBuilding />,         color: '#a855f7' },
    'Utility':        { icon: <FaBolt />,             color: '#f97316' },
    'Maintenance':    { icon: <FaWrench />,           color: '#10b981' },
    'Renovation':     { icon: <FaHome />,             color: '#22c55e' },
    'Other':          { icon: <FaDollarSign />,       color: '#9ca3af' },
  };

  const INCOME_CAT_COLOR = {
    'Rent':           '#2563eb',
    'Fee':            '#a855f7',
    'Uncategorized':  '#f97316',
    'Late Fee':       '#ef4444',
    'Other':          '#9ca3af',
  };
  const INCOME_PALETTE = ['#2563eb','#f97316','#a855f7','#22c55e','#ef4444','#06b6d4','#f59e0b'];
  const getIncomeColor = (name, idx) => INCOME_CAT_COLOR[name] || INCOME_PALETTE[idx % INCOME_PALETTE.length];

  // ── Derived helpers ──────────────────────────────
  const getNoiNum = () => {
    const r = noiData?.result || noiData || {};
    const v = r.noi_ytd ?? r.NOI_YTD ?? r.noi ?? null;
    return v !== null ? Number(v) : null;
  };

  const getNoiYtd = () => {
    const v = getNoiNum();
    if (v === null) return '—';
    return `${v < 0 ? '-' : ''}$${fmt(v)}`;
  };

  const getCapRateNum = () => {
    const r   = capRateData?.result || capRateData || {};
    const pv  = r.property_value ?? null;
    const noiR = noiData?.result || noiData || {};
    const noi  = noiR.noi_ytd ?? noiR.NOI_YTD ?? noiR.noi ?? r.noi_ytd ?? null;
    if (noi === null || pv === null || Number(pv) === 0) return null;
    return (Number(noi) / Number(pv)) * 100;
  };

  const getCapRate = () => {
    const n = getCapRateNum();
    return n !== null ? fmtPct(n) : '—';
  };

  const getCashOnCashNum = () => {
    const r = cashOnCash?.result || cashOnCash || {};
    const v = r.coc_ytd ?? null;
    if (v === null) return null;
    const n = Number(v);
    return n < 1 ? n * 100 : n;
  };

  const getCashOnCash = () => {
    const n = getCashOnCashNum();
    return n !== null ? fmtPct(n) : '—';
  };

  // Returns numeric occupancy (0-100) or null if truly unknown
  const getOccupancyNum = () => {
    // Primary: API now accepts time params — use its percent when available
    const occ = occupancyData || {};
    const apiPct = occ.property_occupancy_percent
      ?? occ.property_occupancy_percent_last_12m
      ?? occ.occupancy_percent
      ?? occ.occupancy_rate
      ?? occ.Occupancy_Rate
      ?? occ.percentage
      ?? null;
    if (apiPct !== null) return Number(apiPct);

    // Fallback: derive from time-filtered rent payments using day-based calculation
    const tp = getTimeParams(filterMode, customMonths, customYears);
    let totalDays = 0;
    for (let y = tp.start_year, m = tp.start_month;;) {
      totalDays += new Date(y, m, 0).getDate();
      if (y === tp.end_year && m === tp.end_month) break;
      m++; if (m > 12) { m = 1; y++; }
    }
    if (totalDays > 0) {
      const now = new Date();
      const todayY = now.getFullYear(); const todayM = now.getMonth() + 1; const todayD = now.getDate();
      // Cap each month at today if it's the current calendar month
      let cappedTotal = 0;
      for (let y = tp.start_year, m = tp.start_month;;) {
        cappedTotal += (y === todayY && m === todayM) ? todayD : new Date(y, m, 0).getDate();
        if (y === tp.end_year && m === tp.end_month) break;
        m++; if (m > 12) { m = 1; y++; }
      }
      const paidMonthKeys = new Set(rentPayments.map(r => {
        const d = new Date(r.Paydate || r.paydate || r.Date || r.date || 0);
        return isNaN(d) ? null : `${d.getFullYear()}-${d.getMonth()}`;
      }).filter(Boolean));
      if (paidMonthKeys.size > 0) {
        let occupiedDays = 0;
        paidMonthKeys.forEach(key => {
          const [yr, mo] = key.split('-').map(Number);
          const isCurrentMo = yr === todayY && (mo + 1) === todayM;
          occupiedDays += isCurrentMo ? todayD : new Date(yr, mo + 1, 0).getDate();
        });
        return (Math.min(occupiedDays, cappedTotal) / cappedTotal) * 100;
      }
      // No rent payments — fall back to property status
      if (property?.status === 'Occupied') return 100;
      if (property?.status === 'Vacant')   return 0;
    }
    return null;
  };

  const getOccupancyRate = () => {
    const n = getOccupancyNum();
    return n === null ? '—' : `${n.toFixed(1)}%`;
  };

  const getNetIncomeYtd = () => {
    const r = netIncomeData?.result || netIncomeData || {};
    const v = r.net_income_ytd ?? r.Net_Income_YTD ?? r.net_income ?? null;
    if (v === null) return '—';
    return `${Number(v) < 0 ? '-' : ''}$${fmt(v)}`;
  };

  const expenseRatioNum = cashFlowSummary.income > 0
    ? (cashFlowSummary.expenses / cashFlowSummary.income) * 100 : 0;

  const comparisonData = (() => {
    const incMap = {}, expMap = {};
    monthlyIncomeData.forEach(i => { incMap[i.rawMonth || i.month] = { label: i.month, value: i.value }; });
    monthlyExpensesData.forEach(e => { expMap[e.rawMonth || e.month] = { label: e.month, value: e.value }; });
    const keys = [...new Set([...Object.keys(incMap), ...Object.keys(expMap)])].sort((a,b) => a.localeCompare(b));
    const uniqueYears = [...new Set(keys.map(k => k.match(/^(\d{4})/)?.[1]).filter(Boolean))];
    const multiYear = uniqueYears.length > 1;
    return keys.map(k => {
      const label = incMap[k]?.label || expMap[k]?.label || formatMonth(k);
      const yr = k.match(/^(\d{4})/)?.[1];
      return {
        month: multiYear && yr ? `${label} '${yr.slice(2)}` : label,
        income: incMap[k]?.value || 0,
        expenses: expMap[k]?.value || 0,
      };
    });
  })();

  const incomeBreakdownData = [...incomeByCat].sort((a, b) => b.value - a.value);

  // ── Modal content ────────────────────────────────
  const periodLabel = { current_month: 'This Month', ytd: 'YTD', trailing_12: 'Trailing 12M', custom: 'Period' }[filterMode] || 'YTD';

  const renderModalContent = () => {
    if (activeModal === 'income') {
      return (
        <>
          <h2 className="rd-modal-title">{filterLabel} — Income</h2>
          <div className="rd-modal-hero rd-modal-hero-green">
            <div className="rd-modal-hero-label">Total Income ({periodLabel})</div>
            <div className="rd-modal-hero-val rd-mhv-green">${fmtFull(cashFlowSummary.income)}</div>
            <div className="rd-modal-hero-sub">{rentPayments.length} payment{rentPayments.length !== 1 ? 's' : ''}</div>
          </div>
          <div className="rd-modal-section-label">Income Details</div>
          <div className="rd-modal-list">
            {rentPayments.length === 0
              ? <div className="rd-modal-empty">No payment records found</div>
              : rentPayments.map((r, i) => {
                  const tenant   = r.Tenant_Name || r.Full_Name || r.tenant_name || r.Door_Name || r.door_name || r.Unit_Name || r.unit_name || r.Property_Name || r.property_name || r.Name || r.name || r.Payee || r.payee || '—';
                  const propName = r.Property_Name || r.property_name || property?.name || '—';
                  const amount   = r.Amount || r.amount || r.Rent || 0;
                  const method   = r.Payment_Method || r.payment_method || 'Cash';
                  const raw      = r.Paydate || r.paydate || r.Date || r.date || '';
                  const dateStr  = raw ? new Date(raw).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—';
                  return (
                    <div key={i} className="rd-modal-item">
                      <div className="rd-modal-item-hd">
                        <div>
                          <span className="rd-modal-item-name">{tenant}</span>
                          <div className="rd-modal-item-sub">{propName}</div>
                        </div>
                        <span className="rd-modal-item-amount" style={{ color: '#16a34a' }}>${fmtFull(amount)}</span>
                      </div>
                      <div className="rd-modal-item-row"><span className="rd-modal-item-label">Payment Method</span><span className="rd-modal-item-val">{method}</span></div>
                      <div className="rd-modal-item-row"><span className="rd-modal-item-label">Date</span><span className="rd-modal-item-val">{dateStr}</span></div>
                    </div>
                  );
                })
            }
          </div>
        </>
      );
    }

    if (activeModal === 'expenses') {
      return (
        <>
          <h2 className="rd-modal-title">{filterLabel} — Expenses</h2>
          <div className="rd-modal-hero rd-modal-hero-red">
            <div className="rd-modal-hero-label">Total Expenses ({periodLabel})</div>
            <div className="rd-modal-hero-val rd-mhv-red">${fmtFull(cashFlowSummary.expenses)}</div>
            <div className="rd-modal-hero-sub">{expensesList.length} expense{expensesList.length !== 1 ? 's' : ''}</div>
          </div>
          <div className="rd-modal-section-label">Expense Details</div>
          <div className="rd-modal-list">
            {expensesList.length === 0
              ? <div className="rd-modal-empty">No expense records found</div>
              : expensesList.map((e, i) => {
                  const title    = e.Title || e.title || e.Vendor || e.vendor || '—';
                  const propName = e.Property_Name || e.property_name || property?.name || '—';
                  const category = e.Category || e.category || '—';
                  const amount   = e.Amount || e.amount || 0;
                  const raw      = e.Paydate || e.paydate || e.Date || e.date || '';
                  const dateStr  = raw ? new Date(raw).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—';
                  return (
                    <div key={i} className="rd-modal-item">
                      <div className="rd-modal-item-hd">
                        <div>
                          <span className="rd-modal-item-name">{title}</span>
                          <div className="rd-modal-item-sub">{propName}</div>
                        </div>
                        <span className="rd-modal-item-amount" style={{ color: '#dc2626' }}>${fmtFull(amount)}</span>
                      </div>
                      <div className="rd-modal-item-row"><span className="rd-modal-item-label">Category</span><span className="rd-modal-item-val">{category}</span></div>
                      <div className="rd-modal-item-row"><span className="rd-modal-item-label">Date</span><span className="rd-modal-item-val">{dateStr}</span></div>
                    </div>
                  );
                })
            }
          </div>
        </>
      );
    }

    if (activeModal === 'net_income') {
      const r       = netIncomeData?.result || netIncomeData || {};
      const niYtd   = r.net_income_ytd ?? r.net_income ?? cashFlowSummary.netCashFlow;
      const niRent  = r.total_rent_ytd || r.total_rent || cashFlowSummary.income;
      const niOpExp = r.non_mort_exp_ytd || r.non_mortgage_expenses || r.operating_expenses || cashFlowSummary.expenses;
      const niMort  = r.mortgage_ytd_used || r.mortgage_payment || propertyFinancials?.Mortgage_Payment || 0;
      const badge      = getBadge('net_income', niYtd);
      const niColor    = Number(niYtd) < 0 ? '#dc2626' : '#16a34a';
      const disclaimer = netIncomeData?.disclaimer || null;
      return (
        <>
          <h2 className="rd-modal-title">Net Income</h2>
          <div className="rd-modal-sub-label">Net Income ({periodLabel})</div>
          <div className="rd-modal-big-row">
            <span className="rd-modal-big-val" style={{ color: niColor }}>{Number(niYtd) < 0 ? '-' : ''}${fmtFull(niYtd)}</span>
            <span className={`rd-mbadge ${badge.cls}`}>{badge.label}</span>
          </div>
          <div className="rd-modal-divider" />
          <div className="rd-modal-rows">
            <div className="rd-mrow"><span className="rd-mrow-icon rd-mri-green"><FaArrowUp /></span><span className="rd-mrow-label">Total Income ({periodLabel})</span><span className="rd-mrow-val">${fmtFull(niRent)}</span></div>
            <div className="rd-mrow"><span className="rd-mrow-icon rd-mri-orange"><FaArrowDown /></span><span className="rd-mrow-label">Non-Mortgage Expenses</span><span className="rd-mrow-val">${fmtFull(niOpExp)}</span></div>
            <div className="rd-mrow"><span className="rd-mrow-icon rd-mri-red"><FaHome /></span><span className="rd-mrow-label">Mortgage ({periodLabel})</span><span className="rd-mrow-val">${fmtFull(niMort)}</span></div>
            <div className="rd-modal-divider" />
            <div className="rd-mrow"><span className="rd-mrow-icon rd-mri-blue"><FaChartLine /></span><span className="rd-mrow-label rd-mrow-bold">Net Income ({periodLabel})</span><span className="rd-mrow-val" style={{ color: niColor, fontWeight: 700 }}>{Number(niYtd) < 0 ? '-' : ''}${fmtFull(niYtd)}</span></div>
          </div>
          <div className="rd-modal-note">{disclaimer || 'Net Income = Total Income – (Non-Mortgage Expenses + Mortgage). Mortgage is only included when an actual expense row with Category = Mortgage exists for the selected period.'}</div>
        </>
      );
    }

    if (activeModal === 'noi') {
      const r      = noiData?.result || noiData || {};
      const noiYtd = r.noi_ytd ?? r.NOI_YTD ?? r.noi ?? 0;
      const noiRent= r.total_rent_ytd || r.total_rent || cashFlowSummary.income;
      const noiExp = r.operating_expenses || r.total_expenses || cashFlowSummary.expenses;
      const noiColor = Number(noiYtd) < 0 ? '#dc2626' : '#16a34a';
      const badge  = getBadge('noi', noiYtd);
      return (
        <>
          <h2 className="rd-modal-title">Net Operating Income</h2>
          <div className="rd-modal-sub-label">NOI ({periodLabel})</div>
          <div className="rd-modal-big-row">
            <span className="rd-modal-big-val" style={{ color: noiColor }}>{Number(noiYtd) < 0 ? '-' : ''}${fmtFull(noiYtd)}</span>
            <span className={`rd-mbadge ${badge.cls}`}>{badge.label}</span>
          </div>
          <div className="rd-modal-divider" />
          <div className="rd-modal-rows">
            <div className="rd-mrow"><span className="rd-mrow-icon rd-mri-green"><FaArrowUp /></span><span className="rd-mrow-label">Total Rent ({periodLabel})</span><span className="rd-mrow-val">${fmtFull(noiRent)}</span></div>
            <div className="rd-mrow"><span className="rd-mrow-icon rd-mri-red"><FaArrowDown /></span><span className="rd-mrow-label">Operating Expenses ({periodLabel})</span><span className="rd-mrow-val">${fmtFull(noiExp)}</span></div>
            <div className="rd-modal-divider" />
            <div className="rd-mrow"><span className="rd-mrow-icon rd-mri-green"><FaChartLine /></span><span className="rd-mrow-label rd-mrow-bold">Net Operating Income ({periodLabel})</span><span className="rd-mrow-val" style={{ color: noiColor, fontWeight: 700 }}>{Number(noiYtd) < 0 ? '-' : ''}${fmtFull(noiYtd)}</span></div>
          </div>
          <div className="rd-modal-note">NOI = Total Rent – Operating Expenses. NOI measures property profitability before debt service and taxes. A positive NOI indicates the property generates enough income to cover operating costs.</div>
        </>
      );
    }

    if (activeModal === 'coc') {
      const r       = cashOnCash?.result || cashOnCash || {};
      const fin     = propertyFinancials || {};
      const downPay = r.down_payment || fin.Down_Payment || fin.down_payment || 0;
      const renov   = r.renov_cost || r.renovation_cost || r.renovation || fin.Renov_Cost || fin.renov_cost || 0;
      const invested= r.total_cash_invested || (Number(downPay) + Number(renov));
      const netIncR = netIncomeData?.result || netIncomeData || {};
      const niYtd   = netIncR.net_income_ytd ?? netIncR.net_income ?? 0;
      const rawCoc  = r.coc_ytd ?? 0;
      const cocPct  = Number(rawCoc) < 1 ? Number(rawCoc) * 100 : Number(rawCoc);
      const cocColor = cocPct < 0 ? '#dc2626' : '#16a34a';
      const badge   = getBadge('coc', cocPct);
      return (
        <>
          <h2 className="rd-modal-title">Cash-on-Cash Return</h2>
          <div className="rd-modal-sub-label">Return on Investment</div>
          <div className="rd-modal-big-row">
            <span className="rd-modal-big-val" style={{ color: cocColor }}>{cocPct.toFixed(1)}%</span>
            <span className={`rd-mbadge ${badge.cls}`}>{badge.label}</span>
          </div>
          <div className="rd-modal-divider" />
          <div className="rd-modal-rows">
            <div className="rd-mrow"><span className="rd-mrow-icon rd-mri-blue"><FaDollarSign /></span><span className="rd-mrow-label">Down Payment</span><span className="rd-mrow-val">${fmtFull(downPay)}</span></div>
            <div className="rd-mrow"><span className="rd-mrow-icon rd-mri-orange"><FaWrench /></span><span className="rd-mrow-label">Renovation & Other Fees</span><span className="rd-mrow-val">${fmtFull(renov)}</span></div>
            <div className="rd-mrow"><span className="rd-mrow-icon rd-mri-blue"><FaChartLine /></span><span className="rd-mrow-label rd-mrow-bold">Total Cash Invested</span><span className="rd-mrow-val">${fmtFull(invested)}</span></div>
            <div className="rd-mrow"><span className="rd-mrow-icon rd-mri-green"><FaArrowUp /></span><span className="rd-mrow-label">Net Income ({periodLabel})</span><span className="rd-mrow-val">${fmtFull(niYtd)}</span></div>
            <div className="rd-modal-divider" />
            <div className="rd-mrow"><span className="rd-mrow-icon rd-mri-purple"><FaPercentage /></span><span className="rd-mrow-label rd-mrow-bold">CoC Return ({periodLabel})</span><span className="rd-mrow-val" style={{ color: cocColor, fontWeight: 700 }}>{cocPct.toFixed(1)}%</span></div>
          </div>
          <div className="rd-modal-note">Cash-on-Cash Return = Net Income ÷ Total Cash Invested (Down Payment + Renovation & Other Fees). Above 8% is generally considered a good return.</div>
        </>
      );
    }

    if (activeModal === 'cap_rate') {
      const r     = capRateData?.result || capRateData || {};
      const noiR  = noiData?.result || noiData || {};
      const crNoi = noiR.noi_ytd ?? noiR.NOI_YTD ?? noiR.noi ?? r.noi_ytd ?? r.noi ?? 0;
      const crPV  = r.property_value || propertyFinancials?.Purchase_Price || propertyFinancials?.purchase_price || 0;
      const crRate= crPV > 0 ? (Number(crNoi) / Number(crPV)) * 100 : 0;
      const crRent= r.total_rent || r.total_rent_ytd || cashFlowSummary.income;
      const crExp = r.total_op_expenses || r.operating_expenses || cashFlowSummary.expenses;
      const crColor = Number(crRate) < 0 ? '#dc2626' : '#16a34a';
      const badge = getBadge('cap_rate', crRate);
      return (
        <>
          <h2 className="rd-modal-title">Capitalization Rate</h2>
          <div className="rd-modal-sub-label">Cap Rate ({periodLabel})</div>
          <div className="rd-modal-big-row">
            <span className="rd-modal-big-val" style={{ color: crColor }}>{Number(crRate).toFixed(2)}%</span>
            <span className={`rd-mbadge ${badge.cls}`}>{badge.label}</span>
          </div>
          <div className="rd-modal-divider" />
          <div className="rd-modal-rows">
            <div className="rd-mrow"><span className="rd-mrow-icon rd-mri-green"><FaChartLine /></span><span className="rd-mrow-label">NOI ({periodLabel})</span><span className="rd-mrow-val">${fmtFull(crNoi)}</span></div>
            <div className="rd-mrow"><span className="rd-mrow-icon rd-mri-blue"><FaBuilding /></span><span className="rd-mrow-label">Property Value</span><span className="rd-mrow-val">${fmtFull(crPV)}</span></div>
            <div className="rd-modal-divider" />
            <div className="rd-mrow"><span className="rd-mrow-icon rd-mri-orange"><FaPercentage /></span><span className="rd-mrow-label rd-mrow-bold">Cap Rate ({periodLabel})</span><span className="rd-mrow-val" style={{ color: crColor, fontWeight: 700 }}>{Number(crRate).toFixed(2)}%</span></div>
            <div className="rd-mrow"><span className="rd-mrow-icon rd-mri-green"><FaArrowUp /></span><span className="rd-mrow-label">Total Rent ({periodLabel})</span><span className="rd-mrow-val">${fmtFull(crRent)}</span></div>
            <div className="rd-mrow"><span className="rd-mrow-icon rd-mri-red"><FaArrowDown /></span><span className="rd-mrow-label">Operating Expenses ({periodLabel})</span><span className="rd-mrow-val">${fmtFull(crExp)}</span></div>
          </div>
          <div className="rd-modal-note">Cap Rate = NOI ÷ Property Value. It estimates the rate of return on a property regardless of financing. A higher cap rate suggests higher potential returns but may also indicate higher risk.</div>
        </>
      );
    }

    if (activeModal === 'occupancy') {
      const occ     = occupancyData || {};
      const occRate = getOccupancyNum() ?? 0;
      const occUnit = occ.doors?.length || occ.total_units || property?.numberOfUnits || 1;
      const tp      = getTimeParams(filterMode, customMonths, customYears);

      // Calculate total days in the selected period, capping the current calendar month at today
      const now2 = new Date();
      const todayY2 = now2.getFullYear(); const todayM2 = now2.getMonth() + 1; const todayD2 = now2.getDate();
      let calcTotalDays = 0;
      for (let y = tp.start_year, m = tp.start_month;;) {
        calcTotalDays += (y === todayY2 && m === todayM2) ? todayD2 : new Date(y, m, 0).getDate();
        if (y === tp.end_year && m === tp.end_month) break;
        m++; if (m > 12) { m = 1; y++; }
      }

      // Calculate occupied days from rent payments, also capping current month at today
      const paidMonthKeys = new Set(rentPayments.map(r => {
        const d = new Date(r.Paydate || r.paydate || r.Date || r.date || 0);
        return isNaN(d) ? null : `${d.getFullYear()}-${d.getMonth()}`;
      }).filter(Boolean));
      let calcOccupiedDays = 0;
      paidMonthKeys.forEach(key => {
        const [yr, mo] = key.split('-').map(Number);
        const isCurrentMo = yr === todayY2 && (mo + 1) === todayM2;
        calcOccupiedDays += isCurrentMo ? todayD2 : new Date(yr, mo + 1, 0).getDate();
      });
      calcOccupiedDays = Math.min(calcOccupiedDays, calcTotalDays);

      // Prefer API data if it returns actual day counts; otherwise use rent-payment calc
      const apiOccDays = occ.property_occupied_days ?? occ.property_occupied_days_last_12m ?? null;
      const apiTotDays = occ.days_elapsed_in_period ?? occ.period_days ?? null;
      const totalDays  = apiTotDays !== null ? Number(apiTotDays) : calcTotalDays;
      // When no rent payments: fall back to property status (Occupied → elapsed days, Vacant → 0)
      const statusOccDays = property?.status === 'Occupied' ? calcTotalDays
                          : property?.status === 'Vacant'   ? 0
                          : calcOccupiedDays;
      const occupiedDays = apiOccDays !== null ? Number(apiOccDays)
                         : paidMonthKeys.size > 0           ? calcOccupiedDays
                         : statusOccDays;

      const periodStr = `${MONTHS[tp.start_month - 1]} ${tp.start_year} – ${MONTHS[tp.end_month - 1]} ${tp.end_year}`;
      const badge     = getBadge('occupancy', occRate);
      const isLow     = Number(occRate) < 50;
      return (
        <>
          <h2 className="rd-modal-title">Occupancy Rate</h2>
          <div className="rd-modal-sub-label">{periodLabel}</div>
          <div className="rd-modal-big-row">
            <span className="rd-modal-big-val" style={{ color: isLow ? '#dc2626' : '#16a34a' }}>{Number(occRate).toFixed(1)}%</span>
            <span className={`rd-mbadge ${badge.cls}`}>{badge.label}</span>
          </div>
          <div className="rd-modal-divider" />
          <div className="rd-modal-rows">
            <div className="rd-mrow"><span className="rd-mrow-icon rd-mri-blue"><FaFileInvoiceDollar /></span><span className="rd-mrow-label">Period</span><span className="rd-mrow-val rd-mrow-small">{periodStr}</span></div>
            <div className="rd-mrow"><span className="rd-mrow-icon rd-mri-blue"><FaChartLine /></span><span className="rd-mrow-label">Occupied Days</span><span className="rd-mrow-val">{occupiedDays} / {totalDays}</span></div>
            <div className="rd-mrow"><span className="rd-mrow-icon rd-mri-purple"><FaBuilding /></span><span className="rd-mrow-label">Total Units</span><span className="rd-mrow-val">{occUnit}</span></div>
          </div>
          <div className="rd-modal-note">Occupancy Rate = Occupied Days ÷ Total Days in the selected period. Days are counted by months with a recorded rent payment. Above 90% is generally considered healthy for residential rental properties.</div>
        </>
      );
    }

    if (activeModal === 'expense_ratio') {
      const erColor = expenseRatioNum < 35 ? '#16a34a' : expenseRatioNum < 50 ? '#65a30d' : expenseRatioNum < 65 ? '#d97706' : expenseRatioNum < 80 ? '#ea580c' : '#dc2626';
      const badge = getBadge('expense_ratio', expenseRatioNum);
      return (
        <>
          <h2 className="rd-modal-title">Expense Ratio</h2>
          <div className="rd-modal-sub-label">Operating Expense Ratio ({periodLabel})</div>
          <div className="rd-modal-big-row">
            <span className="rd-modal-big-val" style={{ color: erColor }}>{expenseRatioNum.toFixed(1)}%</span>
            <span className={`rd-mbadge ${badge.cls}`}>{badge.label}</span>
          </div>
          <div className="rd-modal-divider" />
          <div className="rd-modal-rows">
            <div className="rd-mrow"><span className="rd-mrow-icon rd-mri-green"><FaArrowUp /></span><span className="rd-mrow-label">Total Income ({periodLabel})</span><span className="rd-mrow-val">${fmtFull(cashFlowSummary.income)}</span></div>
            <div className="rd-mrow"><span className="rd-mrow-icon rd-mri-red"><FaArrowDown /></span><span className="rd-mrow-label">Total Expenses ({periodLabel})</span><span className="rd-mrow-val">${fmtFull(cashFlowSummary.expenses)}</span></div>
            <div className="rd-modal-divider" />
            <div className="rd-mrow"><span className="rd-mrow-icon rd-mri-blue"><FaPercentage /></span><span className="rd-mrow-label rd-mrow-bold">Expense Ratio ({periodLabel})</span><span className="rd-mrow-val" style={{ color: erColor, fontWeight: 700 }}>{expenseRatioNum.toFixed(1)}%</span></div>
          </div>
          {expensesByCategory.length > 0 && (
            <>
              <div className="rd-modal-section-label" style={{ marginTop: 16 }}>By Category</div>
              {expensesByCategory.map((item, i) => (
                <div key={i} className="rd-mrow">
                  <span className="rd-mrow-label">{item.category}</span>
                  <span className="rd-mrow-val">${fmtFull(item.amount)}</span>
                </div>
              ))}
            </>
          )}
          <div className="rd-modal-note">Expense Ratio = Total Expenses ÷ Total Income. A lower ratio indicates better operational efficiency. Below 60% is generally considered healthy for rental properties.</div>
        </>
      );
    }

    return null;
  };

  // ── Render ──────────────────────────────────────
  const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const filterLabel = { current_month: 'Current Month', ytd: 'Year to Date', trailing_12: 'Trailing 12 Months', custom: 'Custom Range' }[filterMode] || 'Year to Date';

  if (loading || !property) return <LoadingScreen />;

  return (
    <div className="home-page">
      <Sidebar initials={initials} />
      <div className="main-content">
        <Navbar title="Reports" />

        <div className="rd-container">

          <button className="rd-back-btn" onClick={() => navigate(-1)}>
            <FaArrowLeft /> Back to Properties
          </button>

          {/* Compact header */}
          <div className="rd-report-header">
            <div className="rd-report-header-info">
              <span className="rd-report-name">{property.name}</span>
              <span className="rd-report-sep">·</span>
              <span className="rd-report-addr">{property.address}</span>
              <span className="rd-report-sep">·</span>
              <span className="rd-report-meta">Units: {property.numberOfUnits || 1} &nbsp;·&nbsp; Financial Report – {new Date().getFullYear()}</span>
            </div>
            <button className="rd-filter-btn" onClick={() => setShowFilters(true)}>
              <FaFilter /> Filters
            </button>
          </div>

          {/* 2-column layout */}
          <div className="rd-main-grid">

            {/* LEFT: summary metrics + performance */}
            <div className="rd-main-left">

              <div className="rd-metric-card">
                {[
                  { key: 'income',     label: 'TOTAL INCOME',         sub: filterLabel, val: `$${fmt(cashFlowSummary.income)}`,   valCls: 'rd-mv-income',  accentBg: '#4ade80', rowBg: '#f0fdf4' },
                  { key: 'expenses',   label: 'TOTAL EXPENSES',       sub: filterLabel, val: `$${fmt(cashFlowSummary.expenses)}`, valCls: 'rd-mv-expense', accentBg: '#f87171', rowBg: '#fff5f5' },
                  { key: 'net_income', label: 'NET INCOME',           sub: filterLabel, val: getNetIncomeYtd(),                  valCls: (netIncomeData?.result?.net_income_ytd ?? netIncomeData?.net_income_ytd ?? 0) < 0 ? 'rd-mv-expense' : 'rd-mv-green',   accentBg: '#a3e635', rowBg: null },
                  { key: 'noi',        label: 'NET OPERATING INCOME', sub: filterLabel, val: getNoiYtd(),                        valCls: (getNoiNum() ?? 0) < 0 ? 'rd-mv-expense' : 'rd-mv-green',   accentBg: '#60a5fa', rowBg: null },
                  ...(forgivenessItems.length > 0 ? [{ key: 'forgiven', label: 'TOTAL FORGIVEN', sub: filterLabel, val: `$${fmt(forgivenessItems.reduce((s, r) => s + Math.abs(Number(r.Amount || r.amount || 0)), 0))}`, valCls: 'rd-mv-yellow', accentBg: '#fbbf24', rowBg: null, noClick: true }] : []),
                ].map((m, i) => (
                  <div key={m.key}>
                    {i > 0 && <div className="rd-mrow-divider" />}
                    <div className={`rd-metric-row${m.noClick ? '' : ' rd-clickable'}`} style={m.rowBg ? { background: m.rowBg } : {}} onClick={m.noClick ? undefined : () => setActiveModal(m.key)}>
                      <div className="rd-metric-accent" style={{ background: m.accentBg }} />
                      <div className="rd-metric-info">
                        <span className="rd-metric-lbl">{m.label}</span>
                        <span className="rd-metric-sub">{m.sub}</span>
                      </div>
                      <span className={`rd-metric-val ${m.valCls}`}>{m.val}</span>
                      <span className="rd-metric-chev">›</span>
                    </div>
                  </div>
                ))}
              </div>

              <h3 className="rd-perf-title">Property Performance</h3>
              <div className="rd-metric-card">
                {[
                  { key: 'coc',           label: 'CASH-ON-CASH RETURN', sub: filterLabel,      val: getCashOnCash(),                                                                         color: (getCashOnCashNum() ?? 0) < 0 ? '#dc2626' : '#16a34a' },
                  { key: 'cap_rate',      label: 'CAP RATE',            sub: filterLabel,      val: getCapRate(),                                                                            color: (getCapRateNum() ?? 0) < 0 ? '#dc2626' : '#16a34a' },
                  { key: 'occupancy',     label: 'OCCUPANCY',           sub: filterLabel,      val: getOccupancyRate(),                                                                      color: (getOccupancyNum() ?? 0) > 75 ? '#059669' : '#ea580c' },
                  { key: 'expense_ratio', label: 'EXPENSE RATIO',       sub: filterLabel,      val: cashFlowSummary.income > 0 ? `${expenseRatioNum.toFixed(1)}%` : '—',                   color: expenseRatioNum < 35 ? '#16a34a' : expenseRatioNum < 50 ? '#65a30d' : expenseRatioNum < 65 ? '#d97706' : expenseRatioNum < 80 ? '#ea580c' : '#dc2626' },
                ].map((m, i) => (
                  <div key={m.key}>
                    {i > 0 && <div className="rd-mrow-divider" />}
                    <div className="rd-metric-row rd-clickable" onClick={() => setActiveModal(m.key)}>
                      <div className="rd-metric-accent rd-metric-accent-purple" />
                      <div className="rd-metric-info">
                        <span className="rd-metric-lbl">{m.label}</span>
                        <span className="rd-metric-sub">{m.sub}</span>
                      </div>
                      <span className="rd-metric-val" style={{ color: m.color }}>{m.val}</span>
                      <span className="rd-metric-chev">›</span>
                    </div>
                  </div>
                ))}
              </div>

            </div>{/* end rd-main-left */}

            {/* RIGHT: breakdowns */}
            <div className="rd-main-right">

              <div className="rd-breakdown-card">
                <div className="rd-breakdown-hd">
                  <h3 className="rd-breakdown-title">Income Breakdown</h3>
                  <span className="rd-breakdown-total rd-total-green">${fmt(cashFlowSummary.income)}</span>
                </div>
                {cashFlowSummary.income === 0
                  ? <div className="rd-no-data">No income data</div>
                  : (
                    <div className="rd-expense-list">
                      {incomeBreakdownData.map((item, i) => {
                        const pct   = cashFlowSummary.income > 0 ? ((item.value / cashFlowSummary.income) * 100).toFixed(1) : 0;
                        const color = getIncomeColor(item.name, i);
                        return (
                          <div key={i} className="rd-expense-item">
                            <div className="rd-expense-row">
                              <div className="rd-expense-name-wrap">
                                <span className="rd-exp-cat-icon" style={{ color }}><FaDollarSign /></span>
                                <span className="rd-expense-name">{item.name}</span>
                              </div>
                              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                <span className="rd-expense-pct">{pct}%</span>
                                <span className="rd-expense-amt" style={{ color: '#16a34a' }}>${fmt(item.value)}</span>
                              </div>
                            </div>
                            <div className="rd-expense-bar-wrap">
                              <div className="rd-expense-bar" style={{ width: `${pct}%`, background: color }} />
                            </div>
                          </div>
                        );
                      })}
                      <p className="rd-bd-note">ⓘ Additional income categories will appear when available from your data.</p>
                    </div>
                  )
                }
              </div>

              <div className="rd-breakdown-card">
                <div className="rd-breakdown-hd">
                  <h3 className="rd-breakdown-title">Expense Breakdown</h3>
                  <span className="rd-breakdown-total rd-total-red">${fmt(expBreakdownTotal)}</span>
                </div>
                {sortedCats.length === 0
                  ? <div className="rd-no-data">No expense data</div>
                  : (
                    <div className="rd-expense-list">
                      {sortedCats.map(cat => {
                        const group     = groupedExpenses[cat];
                        const pct       = expBreakdownTotal > 0 ? ((group.total / expBreakdownTotal) * 100).toFixed(1) : 0;
                        const cfg       = CAT_CONFIG[cat] || { icon: <FaEllipsisH />, color: '#6b7280' };
                        const hasSubs   = Object.keys(group.subs).length > 0;
                        const isExpanded = expandedCategories.has(cat);
                        return (
                          <div key={cat} className="rd-expense-item">
                            <div className={`rd-expense-row${hasSubs ? ' rd-exp-clickable' : ''}`} onClick={hasSubs ? () => toggleCategory(cat) : undefined}>
                              <div className="rd-expense-name-wrap">
                                <span className="rd-exp-cat-icon" style={{ color: cfg.color }}>{cfg.icon}</span>
                                <span className="rd-expense-name">{cat}</span>
                                {hasSubs && <span className={`rd-exp-chevron${isExpanded ? ' rd-exp-chevron-up' : ''}`}>^</span>}
                              </div>
                              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                <span className="rd-expense-pct">{pct}%</span>
                                <span className="rd-expense-amt">${fmt(group.total)}</span>
                              </div>
                            </div>
                            <div className="rd-expense-bar-wrap">
                              <div className="rd-expense-bar" style={{ width: `${pct}%`, background: cfg.color }} />
                            </div>
                            {hasSubs && isExpanded && (
                              <div className="rd-exp-sub-list">
                                {Object.entries(group.subs).map(([subCat, subAmt]) => (
                                  <div key={subCat} className="rd-exp-sub-item">
                                    <span className="rd-exp-sub-name">{subCat}</span>
                                    <span className="rd-exp-sub-amt">${fmt(subAmt)}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )
                }
              </div>

            </div>{/* end rd-main-right */}

          </div>{/* end rd-main-grid */}

          {/* ── Charts row ── always show both when any monthly data exists ── */}
          {comparisonData.length > 0 && (() => {
            const fmtK = v => v >= 1000 ? `$${(v/1000).toFixed(0)}K` : (v < 0 && v > -1000) ? `-$${Math.abs(v)}` : v <= -1000 ? `-$${(Math.abs(v)/1000).toFixed(0)}K` : `$${v}`;
            const yAxisProps = { tick: { fontSize: 11, fill: '#9ca3af' }, axisLine: false, tickLine: false, tickFormatter: fmtK, width: 48 };
            const tooltipStyle = { borderRadius: 10, border: '1px solid #e5e7eb', fontSize: 12, background: '#fff' };
            // Fallback: if the cash flow API returned nothing, derive it from income – expenses
            const cashFlowDisplay = monthlyCashFlowData.length > 0
              ? monthlyCashFlowData
              : comparisonData.map(d => ({ month: d.month, value: d.income - d.expenses }));
            return (
              <div className="rd-charts-section">

                {/* Cash Flow line chart */}
                <div className="rd-chart-card">
                  <h3 className="rd-chart-title">Cash Flow</h3>
                  <p className="rd-chart-sub">Net cash in / out per month</p>
                  <div className="rd-chart-area">
                    <ResponsiveContainer width="100%" height={230}>
                      <LineChart data={cashFlowDisplay} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                        <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                        <YAxis {...yAxisProps} />
                        <Tooltip formatter={v => [`$${fmt(v)}`, 'Cash Flow']} contentStyle={tooltipStyle} />
                        <Line
                          type="monotone"
                          dataKey="value"
                          stroke="#2563eb"
                          strokeWidth={2.5}
                          dot={{ r: 5, fill: '#2563eb', strokeWidth: 0 }}
                          activeDot={{ r: 7, fill: '#1d4ed8' }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Monthly Comparison bar chart */}
                <div className="rd-chart-card">
                  <h3 className="rd-chart-title">Monthly Comparison</h3>
                  <p className="rd-chart-sub">Income vs Expenses side by side</p>
                  <div className="rd-chart-area">
                    <ResponsiveContainer width="100%" height={230}>
                      <BarChart data={comparisonData} barGap={3} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                        <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                        <YAxis {...yAxisProps} />
                        <Tooltip formatter={(v, n) => [`$${fmt(v)}`, n === 'income' ? 'Income' : 'Expenses']} contentStyle={tooltipStyle} />
                        <Bar dataKey="income"   fill="#22c55e" radius={[4,4,0,0]} name="income" />
                        <Bar dataKey="expenses" fill="#ef4444" radius={[4,4,0,0]} name="expenses" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="rd-chart-legend">
                    <span className="rd-leg-dot" style={{background:'#22c55e'}} /><span>Income</span>
                    <span className="rd-leg-dot" style={{background:'#ef4444', marginLeft:14}} /><span>Expenses</span>
                  </div>
                </div>

              </div>
            );
          })()}


          <div className="rd-page-footer">
            <span>Data accuracy is based on user input</span>
            <span>Powered by DTecX</span>
          </div>

        </div>

        {/* Filters drawer */}
        {showFilters && (
          <div className="rd-filter-overlay" onClick={() => setShowFilters(false)}>
            <div className="rd-filter-modal" onClick={e => e.stopPropagation()}>
              <div className="rd-filter-hd">
                <h3>Filters</h3>
                <button className="rd-filter-close-btn" onClick={() => setShowFilters(false)}><FaTimes /></button>
              </div>
              <div className="rd-filter-body">
                <p className="rd-filter-lbl">Quick View</p>
                <div className="rd-fq-row">
                  <button className={`rd-fq-btn ${filterMode==='current_month'?'rd-fq-active':''}`} onClick={() => applyFilter('current_month')}>
                    <FaCalendarAlt /> Current Month
                  </button>
                  <button className={`rd-fq-btn ${filterMode==='ytd'?'rd-fq-active':''}`} onClick={() => applyFilter('ytd')}>
                    <FaChartLine /> Year to Date
                  </button>
                </div>
                <button className={`rd-fq-full ${filterMode==='trailing_12'?'rd-fq-active':''}`} onClick={() => applyFilter('trailing_12')}>
                  ⏱ Trailing 12 Months
                </button>

                <p className="rd-filter-lbl" style={{marginTop:20}}>Custom Range</p>
                <div className="rd-filter-range-hd">
                  <span>Months</span>
                  <span className="rd-filter-range-val">{MONTH_NAMES[pendingMonths[0]]} – {MONTH_NAMES[pendingMonths[1]]}</span>
                </div>
                <div className="rd-filter-sel-row">
                  <select className="rd-filter-sel" value={pendingMonths[0]} onChange={e => { const v=parseInt(e.target.value); setPendingMonths([v, Math.max(v, pendingMonths[1])]); }}>
                    {MONTH_NAMES.map((m,i) => <option key={i} value={i}>{m}</option>)}
                  </select>
                  <span className="rd-filter-to">to</span>
                  <select className="rd-filter-sel" value={pendingMonths[1]} onChange={e => { const v=parseInt(e.target.value); setPendingMonths([Math.min(pendingMonths[0],v), v]); }}>
                    {MONTH_NAMES.map((m,i) => <option key={i} value={i}>{m}</option>)}
                  </select>
                </div>

                <div className="rd-filter-range-hd" style={{marginTop:14}}>
                  <span>Year</span>
                  <span className="rd-filter-range-val">{pendingYears[0]} – {pendingYears[1]}</span>
                </div>
                <div className="rd-filter-sel-row">
                  <select className="rd-filter-sel" value={pendingYears[0]} onChange={e => { const v=parseInt(e.target.value); setPendingYears([v, Math.max(v, pendingYears[1])]); }}>
                    {Array.from({length:5},(_,i)=>new Date().getFullYear()-4+i).map(y=><option key={y} value={y}>{y}</option>)}
                  </select>
                  <span className="rd-filter-to">to</span>
                  <select className="rd-filter-sel" value={pendingYears[1]} onChange={e => { const v=parseInt(e.target.value); setPendingYears([Math.min(pendingYears[0],v), v]); }}>
                    {Array.from({length:5},(_,i)=>new Date().getFullYear()-4+i).map(y=><option key={y} value={y}>{y}</option>)}
                  </select>
                </div>

                <button className="rd-filter-apply-btn" onClick={() => applyFilter('custom', pendingMonths, pendingYears)}>Apply Filter</button>
                <button className="rd-filter-clear-btn" onClick={() => applyFilter('ytd')}>Clear Filters</button>
              </div>
            </div>
          </div>
        )}

        {/* Metric modal */}
        {activeModal && (
          <div className="rd-modal-overlay" onClick={closeModal}>
            <div className="rd-modal" onClick={e => e.stopPropagation()}>
              <button className="rd-modal-close" onClick={closeModal}><FaTimes /></button>
              {renderModalContent()}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default ReportDetails;
